import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { NAV_LINKS, SITE, waLink } from "../../data/site";
import { scrollToHash } from "../../lib/scroll";
import { track } from "../../lib/analytics";

const Logo = ({ onClick }) => (
    <Link to="/" onClick={onClick} data-testid="header-logo" className="flex items-center gap-3 shrink-0">
        <img
            src="/images/logo-emblem.png"
            alt="Logo IMPRIMA AKI"
            className="h-10 w-10 rounded-xl object-cover ring-1 ring-line"
        />
        <span className="font-display text-lg font-extrabold tracking-tight text-white">
            IMPRIMA<span className="text-brand"> AKI</span>
        </span>
    </Link>
);

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    const go = (e, hash) => {
        e.preventDefault();
        if (location.pathname !== "/") navigate("/", { state: { scrollTo: hash } });
        else scrollToHash(hash);
        setOpen(false);
    };

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 border-b ${
                    scrolled || open
                        ? "backdrop-blur-xl bg-ink/85 border-line/80"
                        : "bg-transparent border-transparent"
                }`}
            >
                <div className="container-x flex h-[72px] items-center justify-between">
                    <Logo />
                    <nav data-testid="header-nav" className="hidden lg:flex items-center gap-8">
                        {NAV_LINKS.map((l) => (
                            <a
                                key={l.hash}
                                href={`/${l.hash}`}
                                onClick={(e) => go(e, l.hash)}
                                data-testid={`nav-${l.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
                                className="text-sm font-medium text-white/70 hover:text-white transition-colors duration-300"
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>
                    <div className="hidden lg:flex items-center gap-5">
                        <a
                            href={`tel:+55${SITE.whatsapp.slice(2)}`}
                            data-testid="header-phone"
                            className="flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white transition-colors"
                        >
                            <Phone size={15} className="text-brand" />
                            {SITE.phoneDisplay}
                        </a>
                        <a
                            href={waLink()}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => track("whatsapp_click", { location: "header" })}
                            data-testid="header-cta-orcamento"
                            className="group inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-brand-dark"
                        >
                            Pedir orçamento
                            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                    </div>
                    <button
                        data-testid="mobile-menu-button"
                        aria-label={open ? "Fechar menu" : "Abrir menu"}
                        onClick={() => setOpen(!open)}
                        className="lg:hidden flex h-11 w-11 items-center justify-center rounded-full border border-line text-white"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        data-testid="mobile-menu"
                        className="fixed inset-0 z-40 bg-ink lg:hidden"
                    >
                        <div className="flex h-full flex-col justify-between px-6 pt-28 pb-10">
                            <nav className="flex flex-col gap-2">
                                {NAV_LINKS.map((l, i) => (
                                    <motion.a
                                        key={l.hash}
                                        href={`/${l.hash}`}
                                        onClick={(e) => go(e, l.hash)}
                                        initial={{ opacity: 0, x: -24 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.08 + i * 0.06, duration: 0.5 }}
                                        data-testid={`mobile-nav-${l.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
                                        className="font-display text-3xl font-extrabold text-white/90 py-2 hover:text-brand transition-colors"
                                    >
                                        {l.label}
                                    </motion.a>
                                ))}
                            </nav>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.45, duration: 0.5 }}
                                className="flex flex-col gap-4"
                            >
                                <a
                                    href={waLink()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => track("whatsapp_click", { location: "mobile_menu" })}
                                    data-testid="mobile-cta-orcamento"
                                    className="flex items-center justify-center gap-2 rounded-full bg-brand py-4 text-base font-bold text-white"
                                >
                                    Pedir orçamento no WhatsApp
                                    <ArrowUpRight size={18} />
                                </a>
                                <p className="text-center font-mono text-xs tracking-widest text-mist">{SITE.city}</p>
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
